import { FC, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/uk';
import 'dayjs/locale/ru';

import { SvgIcon } from '@/components/SvgIcon';
import { NotificationItem } from '@/redux/models';
import { renderMessageMarkup } from '@/api/amlbotAPI/renderMessageMarkup';
import { resolveNotificationLinkKey } from '@/config/notificationLinks';

import {
  DAYJS_LOCALE,
  ICON_BY_TYPE,
  useNotificationCopy,
} from './notificationDisplay';
import styles from './NotificationBell.module.sass';

dayjs.extend(relativeTime);

interface NotificationRowProps {
  item: NotificationItem;
  // `linkHref` is set when the customer activated a link inside the message: mark the
  // item read, then navigate THERE rather than to the item's own link.
  onSelect: (item: NotificationItem, linkHref?: string) => void;
  // Show the whole message instead of the dropdown's three-line clamp. A message
  // longer than the collapsed height gets a Show more / Show less toggle.
  fullBody?: boolean;
}

export const NotificationRow: FC<NotificationRowProps> = ({
  item,
  onSelect,
  fullBody = false,
}) => {
  const { t, i18n } = useTranslation();

  const copy = useNotificationCopy(item);

  // A personal notification's `link` is a destination KEY, not a path. A plain click
  // opens the notification's popup (see NotificationList), so the destination is also
  // offered here as an explicit link — the same in-message path a campaign link takes.
  // An unknown key, or a legacy row still holding a literal path, resolves to null
  // and renders no link.
  const linkKey = item.kind === 'personal' ? item.link : null;
  const linkPath = resolveNotificationLinkKey(linkKey);

  // Measured rather than decided by a character count: whether a message overflows
  // the collapsed height depends on the page width, and a CSS clamp never cuts
  // through a link or a bold run the way slicing the markup would.
  const bodyRef = useRef<HTMLSpanElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  useLayoutEffect(() => {
    const el = bodyRef.current;
    // Expanded keeps the last answer, so Show less stays on screen.
    if (!fullBody || expanded || !el) return undefined;

    const measure = () => setOverflows(el.scrollHeight > el.clientHeight + 1);
    measure();

    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [fullBody, expanded, copy.body]);

  let bodyClassName = styles.body;
  if (fullBody) {
    bodyClassName += ` ${expanded ? styles.bodyFull : styles.bodyCollapsed}`;
  }

  // Deliberately a div with role="button" rather than a <button>. A campaign message
  // may contain a real <a>, and the HTML content model forbids interactive descendants
  // inside a button — nesting them breaks keyboard navigation and hit-testing. The row
  // keeps its own click, focus and Enter/Space handling so it stays operable without a
  // mouse.
  // A click on a link inside the message is handled by onLinkActivate below, which
  // supplies that link's href. A click anywhere else on the row uses the item's own.
  const activate = (target?: EventTarget | null) => {
    const onLink =
      target instanceof HTMLElement && target.closest('a, button') !== null;
    if (onLink) return;
    onSelect(item);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      className={item.isRead ? styles.row : `${styles.row} ${styles.rowUnread}`}
      data-testid={item.isRead ? 'notification-row' : 'notification-row-unread'}
      onClick={(e) => activate(e.target)}
      onKeyDown={(e) => {
        // Enter on a focused link belongs to the anchor. Swallowing it here made the
        // link completely unreachable by keyboard — which is the exact capability the
        // <button> → div role="button" change existed to enable, so the bug undid its
        // own reason for being. Let the browser follow the link; the click event the
        // anchor's own onClick marks the item read and routes it.
        // The same for the Show more / Show less button: Enter or Space on it toggles
        // the message, not the row.
        const onLink =
          e.target instanceof HTMLElement &&
          e.target.closest('a, button') !== null;
        if (onLink) return;

        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          activate(e.target);
        }
      }}
    >
      {/* No separate unread dot. The row itself carries the unread state (tinted
          background plus a 2px accent edge), which is both more legible than a 6px
          dot and free — the dot was absolutely positioned in a 32px left gutter that
          every row had to reserve, making the whole list look indented. */}
      <span className={styles.icon}>
        <SvgIcon name={ICON_BY_TYPE[item.type] || 'notification'} />
      </span>

      <span className={styles.content}>
        <span className={styles.title}>{copy.title}</span>
        {/* Campaign messages may carry operator-authored markup (bold, italic, link,
            line break); personal notifications are always plain text built from a
            translation, so they are rendered as-is rather than run through a parser
            that would have nothing to do. */}
        <span ref={bodyRef} className={bodyClassName}>
          {item.kind === 'campaign'
            ? renderMessageMarkup(copy.body, {
                // Marks read AND navigates. Passed explicitly because the click
                // cannot be relied on to bubble (the popover unmounts this row as it
                // closes), and because a raw <a href> would full-page-load and abort
                // the mark-read request mid-flight.
                // An external link already opens in a new tab (target=_blank), so
                // it only marks read; passing its href too also navigated THIS tab
                // away to the same site.
                onLinkActivate: (href, external) =>
                  external ? onSelect(item) : onSelect(item, href),
              })
            : copy.body}
        </span>
        {fullBody && overflows && (
          <button
            type="button"
            className={styles.toggle}
            data-testid="notification-toggle"
            aria-expanded={expanded}
            onClick={() => setExpanded((v) => !v)}
          >
            {t(expanded ? 'translation.show_less' : 'translation.show_more')}
          </button>
        )}
        {/* ctaLabel is no longer offered by the compose form — a link now lives inside
            the message markup, where the operator controls its wording and position.
            Still rendered so campaigns sent before that change keep their call to
            action. A span, not a control: the row's own click handles it. */}
        {item.kind === 'campaign' && item.campaign?.ctaLabel && (
          <span className={styles.cta} data-testid="notification-cta">
            {item.campaign.ctaLabel}
          </span>
        )}
        {/* A real <a> so the row's own handlers step aside (they ignore anything
            inside an anchor) and Enter on a focused link works. The click is handled
            here rather than left to the href, for the same reasons as a campaign
            link: a raw href would full-page-load and abort the mark-read request. */}
        {linkPath && (
          <a
            href={linkPath}
            className={styles.cta}
            data-testid="notification-link"
            onClick={(e) => {
              e.preventDefault();
              onSelect(item, linkPath);
            }}
          >
            {t(`translation.notifications.links.${linkKey}`)}
          </a>
        )}
        <span className={styles.time}>
          {dayjs(item.createdAt)
            .locale(DAYJS_LOCALE[i18n.language] || 'en')
            .fromNow()}
        </span>
      </span>
    </div>
  );
};
